if (!customElements.get('stockists-map')) {
  const DIRECTIONS_ICON =
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
    '<circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.6"/>' +
    '<path d="M15.5 8.5L13 13L8.5 15.5L11 11L15.5 8.5Z" fill="currentColor"/>' +
    '</svg>';

  const WEBSITE_ICON =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="M9 6H6a1 1 0 00-1 1v11a1 1 0 001 1h11a1 1 0 001-1v-3"/>' +
    '<path d="M14 4h6v6"/>' +
    '<path d="M10 14L20 4"/>' +
    '</svg>';

  customElements.define(
    'stockists-map',
    class StockistsMap extends HTMLElement {
      connectedCallback() {
        this.mapContainer = this.querySelector('[data-map]');

        let allStockists;
        try {
          allStockists = JSON.parse(this.querySelector('[data-stockists]').textContent);
        } catch (error) {
          console.error('stockists-map: could not parse stockist data JSON.', error);
          if (this.mapContainer) this.mapContainer.textContent = 'The map failed to load. Please refresh the page.';
          return;
        }

        // lat/lng arrive as strings so a mistyped value in the admin (e.g. a URL pasted
        // into the longitude field) can't break the JSON for every stockist. Anything
        // that isn't a finite number becomes null and that stockist just gets no pin.
        allStockists.forEach((stockist) => {
          ['lat', 'lng'].forEach((key) => {
            const value = stockist[key] === '' || stockist[key] == null ? NaN : Number(stockist[key]);
            if (!Number.isFinite(value)) {
              if (stockist[key]) console.warn(`stockists-map: ignoring invalid ${key} "${stockist[key]}" for ${stockist.name}.`);
              stockist[key] = null;
            } else {
              stockist[key] = value;
            }
          });
        });

        this.allStockists = allStockists;
        this.bindSearch();
        this.bindWholesaleLink();

        if (!this.mapContainer) return;

        this.pinned = allStockists
          .map((stockist, index) => ({ stockist, index }))
          .filter(({ stockist }) => typeof stockist.lat === 'number' && typeof stockist.lng === 'number');

        if (this.pinned.length === 0) return;

        this.markers = new Map();
        this.initWhenReady(Date.now());
      }

      initWhenReady(startedAt) {
        if (window.maplibregl && window.Supercluster) {
          this.initMap();
          return;
        }
        if (Date.now() - startedAt > 8000) {
          console.error(
            'stockists-map: MapLibre failed to load within 8s (window.maplibregl is ' +
              typeof window.maplibregl +
              '). Check the Network tab for a failed/blocked request to unpkg.com/maplibre-gl or unpkg.com/supercluster.'
          );
          this.mapContainer.textContent = 'The map failed to load. Please refresh the page.';
          return;
        }
        window.setTimeout(() => this.initWhenReady(startedAt), 50);
      }

      async initMap() {
        this.pinIconMarkup = await this.loadPinIcon();

        this.clusterIndex = new window.Supercluster({ radius: 50, maxZoom: 16 }).load(
          this.pinned.map(({ stockist, index }) => ({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [stockist.lng, stockist.lat] },
            properties: { stockistIndex: index },
          }))
        );

        this.map = new window.maplibregl.Map({
          container: this.mapContainer,
          style: this.dataset.styleUrl,
          scrollZoom: false,
          attributionControl: false,
        });

        this.map.addControl(new window.maplibregl.NavigationControl({ showCompass: false }), 'top-left');

        // The map wrap is sized in vh, which shifts on mobile as the browser chrome
        // (address bar) shows/hides on scroll. MapLibre only syncs its internal
        // transform on window 'resize', which mobile browsers don't reliably fire for
        // chrome-only changes, so a stale transform makes the popup's auto-flip
        // placement miscalculate and land off screen. Watching the container directly
        // catches every size change, chrome-driven or not.
        const resizeObserver = new ResizeObserver(() => this.map.resize());
        resizeObserver.observe(this.mapContainer);

        const bounds = this.pinned.reduce(
          (result, { stockist }) => result.extend([stockist.lng, stockist.lat]),
          new window.maplibregl.LngLatBounds()
        );
        this.map.fitBounds(bounds, { padding: 48, maxZoom: 14, duration: 0 });

        this.map.on('load', () => this.renderMarkers());
        this.map.on('moveend', () => this.renderMarkers());

        this.bindList();
      }

      async loadPinIcon() {
        try {
          const response = await fetch(this.dataset.pinUrl);
          if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
          return await response.text();
        } catch (error) {
          console.error('stockists-map: could not load pin icon, falling back to <img>.', error);
          return null;
        }
      }

      renderMarkers() {
        const zoom = Math.round(this.map.getZoom());
        const bounds = this.map.getBounds();
        const clusters = this.clusterIndex.getClusters(
          [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()],
          zoom
        );

        const nextKeys = new Set();

        clusters.forEach((feature) => {
          const [lng, lat] = feature.geometry.coordinates;
          const isCluster = Boolean(feature.properties.cluster);
          const key = isCluster
            ? `cluster-${feature.properties.cluster_id}`
            : `point-${feature.properties.stockistIndex}`;
          nextKeys.add(key);

          if (this.markers.has(key)) return;

          const marker = isCluster
            ? this.buildClusterMarker(feature, lng, lat)
            : this.buildPointMarker(this.allStockists[feature.properties.stockistIndex], lng, lat);

          this.markers.set(key, marker);
        });

        this.markers.forEach((marker, key) => {
          if (!nextKeys.has(key)) {
            marker.remove();
            this.markers.delete(key);
            if (this.activeMarker === marker) this.activeMarker = null;
          }
        });

        this.resolvePendingActivation();
      }

      buildClusterMarker(feature, lng, lat) {
        const count = feature.properties.point_count;
        const el = document.createElement('button');
        el.type = 'button';
        el.className = 'stockists-map__cluster';
        el.innerHTML = `<span class="stockists-map__cluster-inner">${count}</span>`;
        el.setAttribute('aria-label', `${count} stockists in this area, zoom in to see them`);
        el.addEventListener('click', () => {
          const expansionZoom = Math.min(
            this.clusterIndex.getClusterExpansionZoom(feature.properties.cluster_id),
            20
          );
          this.map.easeTo({ center: [lng, lat], zoom: expansionZoom });
        });

        return new window.maplibregl.Marker({ element: el, anchor: 'center' }).setLngLat([lng, lat]).addTo(this.map);
      }

      buildPointMarker(stockist, lng, lat) {
        const el = document.createElement('button');
        el.type = 'button';
        el.className = 'stockists-map__marker';
        el.setAttribute('aria-label', stockist.name);
        el.innerHTML = this.pinIconMarkup
          ? this.pinIconMarkup
          : `<img src="${this.dataset.pinUrl}" alt="" width="34" height="33" loading="lazy">`;

        const popup = new window.maplibregl.Popup({
          offset: 20,
          className: 'stockists-map__marker-popup',
          maxWidth: 'none',
        }).setDOMContent(this.popupHtml(stockist));

        const marker = new window.maplibregl.Marker({ element: el, anchor: 'center' })
          .setLngLat([lng, lat])
          .setPopup(popup)
          .addTo(this.map);

        el.addEventListener('click', (event) => {
          event.stopPropagation();
          this.togglePopupExclusive(marker);
        });

        return marker;
      }

      togglePopupExclusive(marker) {
        const popup = marker.getPopup();
        const wasOpen = Boolean(popup && popup.isOpen());

        if (this.activeMarker && this.activeMarker !== marker) {
          const activePopup = this.activeMarker.getPopup();
          if (activePopup && activePopup.isOpen()) activePopup.remove();
        }

        marker.togglePopup();
        this.activeMarker = wasOpen ? null : marker;
      }

      popupHtml(stockist) {
        const wrapper = document.createElement('div');
        wrapper.className = 'stockists-map__popup';

        const header = document.createElement('div');
        header.className = 'stockists-map__popup-header';

        const logoWrap = document.createElement('span');
        if (stockist.logo) {
          logoWrap.className = 'stockists-map__popup-logo';
          const logoImg = document.createElement('img');
          logoImg.src = stockist.logo;
          logoImg.alt = '';
          logoImg.loading = 'lazy';
          logoWrap.appendChild(logoImg);
        } else {
          logoWrap.className = 'stockists-map__popup-logo stockists-map__popup-logo--placeholder';
          logoWrap.setAttribute('aria-hidden', 'true');
          logoWrap.textContent = stockist.name ? stockist.name.charAt(0).toUpperCase() : '';
        }
        header.appendChild(logoWrap);

        const info = document.createElement('div');
        info.className = 'stockists-map__popup-info';

        const name = document.createElement('strong');
        name.className = 'stockists-map__popup-name';
        name.textContent = stockist.name;
        info.appendChild(name);

        if (stockist.town) {
          const town = document.createElement('span');
          town.className = 'stockists-map__popup-town';
          town.textContent = stockist.town;
          info.appendChild(town);
        }

        header.appendChild(info);
        wrapper.appendChild(header);

        const actions = document.createElement('div');
        actions.className = 'stockists-map__popup-actions';

        const directions = document.createElement('a');
        directions.className = 'stockists-map__popup-btn stockists-map__popup-btn--primary';
        directions.href = `https://www.google.com/maps/dir/?api=1&destination=${stockist.lat},${stockist.lng}`;
        directions.target = '_blank';
        directions.rel = 'noopener';
        directions.innerHTML = `${DIRECTIONS_ICON}<span>Directions</span>`;
        actions.appendChild(directions);

        if (stockist.website) {
          const website = document.createElement('a');
          website.className = 'stockists-map__popup-btn stockists-map__popup-btn--secondary';
          website.href = stockist.website;
          website.target = '_blank';
          website.rel = 'noopener';
          website.innerHTML = `${WEBSITE_ICON}<span>Website</span>`;
          actions.appendChild(website);
        }

        wrapper.appendChild(actions);

        return wrapper;
      }

      bindList() {
        const items = this.querySelectorAll('[data-stockist-index]');
        items.forEach((item) => {
          const index = Number(item.dataset.stockistIndex);
          const target = this.allStockists[index];
          const hasPin = target && typeof target.lat === 'number' && typeof target.lng === 'number';

          if (!hasPin) {
            item.setAttribute('aria-disabled', 'true');
            return;
          }

          const activate = () => {
            this.pendingActivation = { index, attempts: 0 };
            // Supercluster's maxZoom is 16 (see initMap), so 17 always lands past the
            // last clustering level in one hop instead of needing the retry easeTo
            // below to zoom in step by step. duration: 0 jumps straight there with
            // no pan/zoom animation.
            this.map.flyTo({ center: [target.lng, target.lat], zoom: Math.max(this.map.getZoom(), 17), duration: 0 });
          };

          item.addEventListener('click', activate);
          item.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              activate();
            }
          });
        });
      }

      bindSearch() {
        const input = this.querySelector('[data-stockists-search]');
        const counter = this.querySelector('[data-stockists-count]');
        const emptyState = this.querySelector('[data-stockists-empty]');
        const items = Array.from(this.querySelectorAll('[data-stockist-index]'));

        if (!input || !items.length) return;

        const total = items.length;

        const filter = () => {
          const query = input.value.trim().toLowerCase();
          let visible = 0;

          items.forEach((item) => {
            const stockist = this.allStockists[Number(item.dataset.stockistIndex)];
            const haystack = `${stockist.name} ${stockist.town}`.toLowerCase();
            const matches = query === '' || haystack.includes(query);
            item.style.display = matches ? '' : 'none';
            if (matches) visible += 1;
          });

          if (counter) counter.textContent = `Showing ${visible} of ${total}`;
          if (emptyState) emptyState.hidden = visible !== 0;
        };

        input.addEventListener('input', filter);
      }

      bindWholesaleLink() {
        const link = this.querySelector('[data-wholesale-link]');
        if (!link) return;

        link.addEventListener('click', (event) => {
          // Shopify prefixes JSON-template section ids with the template id at render
          // time (e.g. "template--123__multicolumn_wholesaleCta"), so the href's literal
          // "#WholesaleCta-<id>" anchor set in the schema rarely matches the live DOM.
          // The data attribute is a stable hook that doesn't depend on that id.
          const target =
            document.querySelector('[data-wholesale-cta-target]') || document.querySelector(link.getAttribute('href') || '');
          if (!target) return;

          event.preventDefault();
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }

      resolvePendingActivation() {
        if (!this.pendingActivation) return;

        const { index, attempts } = this.pendingActivation;
        const marker = this.markers.get(`point-${index}`);

        if (marker) {
          marker.togglePopup();
          this.pendingActivation = null;
          return;
        }

        if (attempts >= 4) {
          this.pendingActivation = null;
          return;
        }

        this.pendingActivation = { index, attempts: attempts + 1 };
        this.map.easeTo({ zoom: Math.min(this.map.getZoom() + 2, 20), duration: 0 });
      }
    }
  );
}
